// AppShellHost — connection-health state machine (offline round Stage A).
//
// Extracted verbatim from AppShellHost.tsx (C1 decomposition). Owns the
// offline-detection + backoff-retry machinery: a read outcome is folded into
// the standing `health` fact — a transient transport error (retriable per
// `toDisplay`) flips health offline and schedules a backoff+jitter retry; a
// genuine ok returns it online. A terminal error is NOT a connectivity fact and
// is handled by the caller (it flows to `libraryError`). The `retrySeq` bump is
// the re-read signal every load effect subscribes to; `retryNow` bypasses the
// backoff timer for author agency.

import {
    type Dispatch,
    type SetStateAction,
    useEffect,
    useRef,
    useState,
} from "react";
import type {
    Health,
    HealthReason,
} from "../../components/Health/ConnectionIndicator.js";
import { type ExternalError, toDisplay } from "../externalError.js";
import { RETRY_BASE_MS, RETRY_CAP_MS } from "./constants.js";

// Exponential backoff with full jitter for offline read retries (offline round
// §3, lesson 7): plain backoff self-synchronises into a retry storm, so the
// delay is a RANDOM point in [0, cap-capped exponential]. Base 1s, doubling,
// capped at 30s. The randomness lives here (called only from the impure retry
// scheduler in the shell), never in the pure core.
const backoffDelay = (attempt: number): number => {
    const ceiling = Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** attempt);
    return Math.round(ceiling * Math.random());
};

export type ConnectionHealth = {
    readonly health: Health;
    readonly retrySeq: number;
    readonly setRetrySeq: Dispatch<SetStateAction<number>>;
    readonly lastCheckedAt: number | null;
    // Why the last read failed: the device has no network, or the server
    // didn't answer while the network is up. Read from `navigator.onLine` at
    // the failure (it can't prove the server is up, but a false is reliable).
    readonly reason: HealthReason;
    // When the pending backoff retry fires (epoch ms); null while none is
    // scheduled (online, or a retry in flight).
    readonly nextRetryAt: number | null;
    readonly foldReadHealth: (ok: boolean) => void;
    readonly isTransient: (e: ExternalError) => boolean;
    readonly retryNow: () => void;
};

export const useConnectionHealth = (): ConnectionHealth => {
    // Bumped by a failed pane's "Try again" AND by the offline backoff timer —
    // re-runs the load effects (offline round Stage A).
    const [retrySeq, setRetrySeq] = useState(0);
    // Connection health, folded from read outcomes: a transient read failure
    // (Unreachable/Upstream) flips it offline and schedules a backoff retry; the
    // next ok returns it online. `online` is the resting state (the indicator
    // renders nothing). Stage-A shortcut: writes made while offline still don't
    // survive a reload — that needs the Stage-B client store + outbox.
    const [health, setHealth] = useState<Health>("online");
    // When the last health check ran (ms), stamped on every read outcome +
    // heartbeat tick. Shown in the health popover so an offline author knows
    // how stale the status is. The host owns the clock; the component gets a
    // pre-formatted string.
    const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(null);
    const [reason, setReason] = useState<HealthReason>("server");
    const [nextRetryAt, setNextRetryAt] = useState<number | null>(null);
    // Backoff bookkeeping: the failed-attempt count (drives the delay) and the
    // pending retry timer, so a single retry is in flight at a time (never a
    // retry storm) and it's cleared on success / unmount.
    const retryAttemptRef = useRef(0);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Which health-folded reads are currently failing. Health is offline while
    // EITHER is failing and returns online only when BOTH have recovered — so a
    // retry that fixes one read never prematurely stops the loop for the other.
    const readFailingRef = useRef(false);

    const isTransient = (e: ExternalError): boolean => toDisplay(e).retriable;

    const clearRetryTimer = () => {
        if (retryTimerRef.current !== null) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        setNextRetryAt(null);
    };

    // Schedule the next read attempt on backoff+jitter, guarded so only ONE
    // retry is pending at a time (both load effects share this — never a retry
    // storm). While the timer waits, health reads "retrying"; the tick bumps
    // retrySeq, which re-runs the load effects.
    const scheduleRetry = () => {
        if (retryTimerRef.current !== null) return;
        const delay = backoffDelay(retryAttemptRef.current);
        retryAttemptRef.current += 1;
        setNextRetryAt(Date.now() + delay);
        retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            setNextRetryAt(null);
            setHealth("retrying");
            setRetrySeq((s) => s + 1);
        }, delay);
    };

    // One read reported success (`ok`) or a transient failure. There is only
    // ONE read now (`loadLibrary` — no separate `loadProjects` once Project
    // retired as a container), so health is a direct fold, not a per-read
    // union.
    const foldReadHealth = (ok: boolean) => {
        // Every read outcome IS a health check — stamp when it happened.
        setLastCheckedAt(Date.now());
        readFailingRef.current = !ok;
        if (readFailingRef.current) {
            setReason(navigator.onLine ? "server" : "network");
            setHealth("offline");
            scheduleRetry();
        } else {
            retryAttemptRef.current = 0;
            clearRetryTimer();
            setHealth("online");
        }
    };

    // Retry NOW — bypass the backoff timer entirely (offline-robustness §4:
    // the author gets agency instead of waiting on exponential backoff). Cancel
    // the pending backoff tick, reset the attempt count so a fresh sequence
    // starts if this attempt also fails, and re-run the load effects at once.
    const retryNow = () => {
        clearRetryTimer();
        retryAttemptRef.current = 0;
        setHealth("retrying");
        setRetrySeq((s) => s + 1);
    };

    // The network came back: retry at once instead of waiting out the backoff
    // (the browser's `online` event is a reliable "worth trying now").
    // biome-ignore lint/correctness/useExhaustiveDependencies: retryNow only touches refs + setters; the listener reads the latest health through the ref below.
    useEffect(() => {
        const onOnline = () => {
            if (readFailingRef.current) retryNow();
        };
        window.addEventListener("online", onOnline);
        return () => window.removeEventListener("online", onOnline);
    }, []);

    // Clear any pending retry when the host unmounts.
    // biome-ignore lint/correctness/useExhaustiveDependencies: clearRetryTimer only touches a ref; a mount-once cleanup is intended.
    useEffect(() => () => clearRetryTimer(), []);

    return {
        health,
        retrySeq,
        setRetrySeq,
        lastCheckedAt,
        reason,
        nextRetryAt,
        foldReadHealth,
        isTransient,
        retryNow,
    };
};
